import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { ModalPanel } from "@/components/ui/modal-panel";
import { CloseButton } from "@/components/ui/close-button";
import { FileText, ListTodo, FolderOpen, User } from "lucide-react";
import type { ChatSource, ChatSourceMetadata } from "@shared/api";

const iconMap: Record<ChatSource["entity_type"], React.ElementType> = {
  note: FileText,
  task: ListTodo,
  project: FolderOpen,
  member: User,
};

interface SourceModalProps {
  source: ChatSource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatDate(date?: string | null) {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US").replace(/\//g, "-");
}

function Info({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[15px] uppercase tracking-wide text-purple-300">
        {label}
      </span>
      <div className="rounded-md bg-window/60 px-3 py-1.5 text-purple-100 text-sm leading-snug">
        {value || "-"}
      </div>
    </div>
  );
}

function NoteContent({ source, meta }: { source: ChatSource; meta: ChatSourceMetadata }) {
  const clientName = meta.client_name ?? "";
  const location = [meta.city, meta.state].filter(Boolean).join(", ");
  const projectName = source.project_name ?? "";

  return (
    <>
      {(clientName || projectName || location) && (
        <div className="text-[11px] text-purple-300 uppercase tracking-wide leading-snug pr-10">
          <div>{clientName || "-"}</div>
          {(location || projectName) && (
            <div>
              {location || "-"}
              {projectName ? ` - ${projectName}` : ""}
            </div>
          )}
        </div>
      )}

      <div className="my-3 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="text-sm text-muted-foreground leading-relaxed max-h-[50vh] overflow-y-auto">
        {meta.content || source.preview || <span className="italic">No content available.</span>}
      </div>

      {meta.created_at && (
        <div className="mt-auto pt-3 border-t border-white/5 text-[11px] text-white/50 font-medium tracking-wide">
          {formatDate(meta.created_at)}
        </div>
      )}
    </>
  );
}

function TaskContent({ source, meta }: { source: ChatSource; meta: ChatSourceMetadata }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-x-10 gap-y-1 text-sm">
        <div className="flex flex-col gap-3">
          <Info label="Client:" value={meta.client_name} />
          <Info label="Location:" value={[meta.city, meta.state].filter(Boolean).join(", ") || null} />
          <Info label="Project:" value={source.project_name} />
          <Info label="Task Created" value={formatDate(meta.created_at)} />
        </div>
        <div className="flex flex-col gap-3">
          <Info label="Discipline:" value={meta.discipline} />
          <Info label="Type:" value={meta.task_type} />
          <Info label="Status:" value={meta.status} />
          <Info label="Task Due:" value={formatDate(meta.due_date)} />
        </div>
      </div>

      <div className="flex flex-col gap-1 mt-3">
        <span className="text-[15px] uppercase tracking-wide text-purple-300">
          Description
        </span>
        <div className="rounded-md bg-window/60 px-3 py-2 text-purple-100 text-sm leading-snug max-h-[40vh] overflow-y-auto">
          {meta.description || source.preview || "No description"}
        </div>
      </div>
    </>
  );
}

function GenericSourceContent({ source }: { source: ChatSource }) {
  const label = source.entity_type === "project" ? "Project"
    : source.entity_type === "member" ? "Team Member"
    : source.entity_type;

  return (
    <>
      {source.project_name && (
        <p className="text-sm text-muted-foreground">{label} &middot; {source.project_name}</p>
      )}
      {source.preview ? (
        <div className="whitespace-pre-wrap break-words text-sm leading-relaxed max-h-[60vh] overflow-y-auto text-purple-100">
          {source.preview}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">No content available.</p>
      )}
    </>
  );
}

export default function SourceModal({ source, open, onOpenChange }: SourceModalProps) {
  const [note, setNote] = useState<Note | null>(null);
  const [task, setTask] = useState<Task | null>(null);
  const [project, setProject] = useState<ProjectSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !source) {
      setNote(null);
      setTask(null);
      setProject(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    console.log("[source-pill:modal] opening", {
      entity_type: source.entity_type,
      entity_id: source.entity_id,
      title: source.title,
      project_id: source.project_id,
    });

    (async () => {
      try {
        const projectsPromise = source.project_id
          ? listProjects().then((ps) => ps.find((p) => p.id === source.project_id) ?? null)
          : Promise.resolve(null);

        if (source.entity_type === "note") {
          const [n, proj] = await Promise.all([getNoteById(source.entity_id), projectsPromise]);
          if (cancelled) return;
          console.log("[source-pill:modal] note result", { id: source.entity_id, found: !!n });
          setNote(n);
          setProject(proj);
        } else if (source.entity_type === "task") {
          const [t, proj] = await Promise.all([getTaskById(source.entity_id), projectsPromise]);
          if (cancelled) return;
          console.log("[source-pill:modal] task result", { id: source.entity_id, found: !!t });
          setTask(t);
          setProject(proj);
        }
      } catch (e: any) {
        console.error("[source-pill:modal] fetch error", { entity_id: source.entity_id, error: e?.message });
        if (!cancelled) setError(e?.message ?? "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, source?.entity_id, source?.entity_type]);

  if (!source) return null;

  const Icon = iconMap[source.entity_type] ?? FileText;
  const close = () => onOpenChange(false);
  const meta = source.metadata ?? {};

  const isNote = source.entity_type === "note";
  const isTask = source.entity_type === "task";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[500px] w-fit bg-transparent border-none p-0 [&>button]:hidden">
        <ModalPanel className="h-full rounded-3xl p-6">
          <div className="flex items-center justify-between pb-3 mb-4 border-b border-purple-500/10">
            <DialogTitle className="flex items-center gap-2 text-purple-200 text-[18px] font-semibold">
              <Icon className="w-4 h-4 shrink-0" />
              {source.title}
            </DialogTitle>
            <CloseButton onClick={close} />
          </div>

          {isNote && <NoteContent source={source} meta={meta} />}
          {isTask && <TaskContent source={source} meta={meta} />}
          {!isNote && !isTask && <GenericSourceContent source={source} />}
        </ModalPanel>
      </DialogContent>
    </Dialog>
  );
}
